import * as RHC from "@radix-ui/react-hover-card";
import type { JSX, ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

interface HoverCardProps {
  content: ReactNode;
  children: ReactNode;
  /** Defaults to 500ms — Radix's own default is 700ms, ~300ms is Tooltip's territory.
   * 500ms avoids flashing cards while scanning a dense table. */
  openDelay?: number;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}

/** Richer sibling of `Tooltip`: for structured/multi-field content (a label/value
 * grid, a preview) rather than a short plain-text hint. No `Provider` needed —
 * unlike Radix `Tooltip`, `HoverCard` manages its own open state per instance. */
export function HoverCard({
  content,
  children,
  openDelay = 500,
  side = "top",
  className,
}: HoverCardProps): JSX.Element {
  return (
    <RHC.Root openDelay={openDelay}>
      <RHC.Trigger asChild>{children}</RHC.Trigger>
      <RHC.Portal>
        <RHC.Content
          side={side}
          sideOffset={6}
          className={cn(
            "z-50 w-60 rounded-lg bg-[var(--ink-900)] px-3 py-2.5 text-xs text-[var(--text-inverse)] shadow-[var(--sh-2)]",
            className,
          )}
        >
          {content}
          <RHC.Arrow className="fill-[var(--ink-900)]" />
        </RHC.Content>
      </RHC.Portal>
    </RHC.Root>
  );
}
